/**
 * The application shell's lazy parts that render with the first screen.
 *
 * The app routes load AppLayout itself lazily, so the login form's startup
 * code leaves it out, and AppLayout loads its parts with `React.lazy`. On
 * their own they would start only once the route and then the layout
 * render. Starting them
 * with the view (on a reload) or while the login form waits for input lets
 * the first screen render without that second round of module loads. The
 * loaders are the ones AppLayout uses, so a started load is the same module.
 */
import { cachedModule } from '@/platform/lib/cachedModule';

export const loadAppLayoutModule = cachedModule(() => import('./AppLayout'));
export const loadDesktopSidebarModule = cachedModule(() => import('./Sidebar/DesktopSidebar'));
export const loadSidebarPanelModule = cachedModule(() => import('./Sidebar/SidebarPanel'));
export const loadMobileNavigationModule = () => import('@/platform/components/mobile');
export const loadSearchProviderRegistryModule = () =>
  import('@/platform/search/SearchProviderRegistry');
export const loadDocPresenceManagerModule = () =>
  import('@/app/docs/components/DocPresenceManager');
export const loadTeamChatNotificationsManagerModule = () =>
  import('./TeamChatNotificationsManager');

// Tailwind's `md` breakpoint, where AppLayout swaps the mobile navigation for
// the desktop sidebar.
const DESKTOP_QUERY = '(min-width: 768px)';

function isDesktop(): boolean {
  try {
    return window.matchMedia(DESKTOP_QUERY).matches;
  } catch {
    return true;
  }
}

/**
 * Start loading the shell parts the first screen renders. Settles once they
 * have loaded or failed; never rejects.
 */
export function preloadAppShell(): Promise<void> {
  const loaders = [
    loadAppLayoutModule,
    ...(isDesktop()
      ? [loadDesktopSidebarModule, loadSidebarPanelModule]
      : [loadMobileNavigationModule]),
    loadSearchProviderRegistryModule,
    loadDocPresenceManagerModule,
    loadTeamChatNotificationsManagerModule,
  ];
  return Promise.all(
    loaders.map(load =>
      load().then(
        () => undefined,
        error => console.warn('[appShell] preload failed', error)
      )
    )
  ).then(() => undefined);
}
